/**
 * Vérification d'identité récente (socle, 29/10/2026) – côté front. Avant une action sensible, l'API répond 403
 * `verification_requise` ; `avecVerification(action)` demande alors à l'utilisateur de se reconnecter à Microsoft
 * (fenêtre de confirmation du design, puis petite fenêtre Microsoft), attend le retour et rejoue l'action une fois.
 *   const { avecVerification } = useVerificationIdentite();
 *   const secret = await avecVerification(() => api.post('/v1/outils/trousseau/entrees/42/affichage', {}));
 *   // undefined : l'utilisateur a renoncé (ou la fenêtre a été bloquée) – rien à afficher
 * Fenêtre : ouverte VIDE dès le clic de confirmation (sinon le navigateur la bloque après un appel réseau), puis
 * envoyée sur Entra ; Entra la renvoie sur ROUTE_VERIFICATION_RETOUR (écran public RetourVerification), qui passe code
 * et état à cette page par BroadcastChannel (même origine, indépendant des politiques d'ouverture de Microsoft) et se
 * ferme. Côté API : api/src/core/verification.ts.
 * Fenêtre bloquée par le navigateur (bloqueur, application installée) : REPLI dans l'onglet – la page part sur Entra et
 * revient sur l'écran de retour, qui rend la main à la page de départ ; <FinVerification /> (monté par la coquille)
 * termine alors la vérification et invite à refaire l'action (la preuve vaut 5 minutes).
 */
import React from 'react';
import {
  CANAL_VERIFICATION, DUREE_VERIFICATION_MIN, ROUTE_VERIFICATION_RETOUR, schemaMessageVerification,
  type DebutVerification, type EtatVerification, type MessageVerification,
} from '@agora/shared';
import { Button } from '../../design';
import { useT } from '../i18n';
import { ErreurApi } from '../api/client';
import { useConfirmation, useMessageEphemere } from '../retours/Retours';
import { useSocle } from '../contexte';
import { useQuitterAgora } from '../pages/espace';

/** Délai maximal de la reconnexion dans la fenêtre Microsoft (durée de l'état côté API). */
const ATTENTE_MAX_MS = 10 * 60_000;
/** Repli dans l'onglet (sessionStorage, propre à l'onglet) : page à retrouver, puis code et état reçus d'Entra. */
const CLE_REPLI = 'agora.verification.repli';
const CLE_RESULTAT = 'agora.verification.resultat';

const lireSession = <T,>(cle: string): T | null => {
  try {
    return JSON.parse(sessionStorage.getItem(cle) || 'null') as T | null;
  } catch {
    return null;
  }
};

/** Attend le message de la fenêtre de retour pour cet état (ou null : délai dépassé, attente annulée). */
function attendreRetour(etat: string, signal: AbortSignal): Promise<MessageVerification | null> {
  return new Promise((resoudre) => {
    const canal = new BroadcastChannel(CANAL_VERIFICATION);
    const fin = (m: MessageVerification | null) => {
      window.clearTimeout(minuterie);
      canal.close();
      resoudre(m);
    };
    const minuterie = window.setTimeout(() => fin(null), ATTENTE_MAX_MS);
    signal.addEventListener('abort', () => fin(null), { once: true });
    canal.onmessage = (e: MessageEvent) => {
      const m = schemaMessageVerification.safeParse(e.data);
      if (m.success && (m.data.etat === etat || (!m.data.etat && m.data.erreur))) fin(m.data);
    };
  });
}

export function useVerificationIdentite() {
  const { api } = useSocle();
  const { t } = useT();
  const confirmer = useConfirmation();
  const afficher = useMessageEphemere();
  const quitterAgora = useQuitterAgora();
  const enCours = React.useRef<AbortController | null>(null);
  React.useEffect(() => () => enCours.current?.abort(), []);

  /** Demande la reconnexion ; true si l'identité est confirmée pour cette session. */
  const verifier = React.useCallback(async (): Promise<boolean> => {
    const ok = await confirmer({
      titre: t('verification.titre'),
      texte: t('verification.texte', { minutes: DUREE_VERIFICATION_MIN }),
      confirmer: t('verification.continuer'),
    });
    if (!ok) return false;
    enCours.current?.abort();
    const controle = new AbortController();
    enCours.current = controle;
    // Ouverte dans le geste de l'utilisateur (clic de confirmation), avant tout appel réseau.
    const fenetre = window.open('', 'agora-verification', 'popup,width=520,height=720');
    if (!fenetre) {
      // Repli : la vérification se fait dans cet onglet, puis l'utilisateur revient sur cette page – après confirmation
      // si un appel, un enregistrement ou un brouillon serait perdu.
      const { adresse } = await api.post<DebutVerification>('/v1/verification', {});
      await quitterAgora(() => {
        sessionStorage.setItem(CLE_REPLI, JSON.stringify({ retour: window.location.pathname + window.location.search + window.location.hash }));
        window.location.assign(adresse);
      });
      return false;
    }
    let debut: DebutVerification;
    try {
      debut = await api.post<DebutVerification>('/v1/verification', {});
    } catch (e) {
      fenetre.close();
      throw e;
    }
    const etat = new URL(debut.adresse, window.location.origin).searchParams.get('state') ?? '';
    const attente = attendreRetour(etat, controle.signal);
    fenetre.location.href = debut.adresse;
    afficher({ statut: 'info', titre: t('verification.enCours') });
    const m = await attente;
    if (enCours.current === controle) enCours.current = null;
    if (!m) return false;
    if (m.erreur || !m.code || !m.etat) {
      afficher({ statut: 'danger', titre: t('verification.retourErreur') });
      return false;
    }
    const r = await api.post<EtatVerification>('/v1/verification/retour', { code: m.code, etat: m.etat });
    return !!r.valideJusqua;
  }, [afficher, api, confirmer, quitterAgora, t]);

  /** Exécute l'action ; si l'API exige une vérification, la fait puis rejoue l'action une fois (undefined si abandon). */
  const avecVerification = React.useCallback(async <T,>(action: () => Promise<T>): Promise<T | undefined> => {
    try {
      return await action();
    } catch (e) {
      if (!(e instanceof ErreurApi && e.code === 'verification_requise')) throw e;
      if (!(await verifier())) return undefined;
      return action();
    }
  }, [verifier]);

  return { verifier, avecVerification };
}

/**
 * Écran public de retour de la fenêtre de vérification (ROUTE_VERIFICATION_RETOUR, hors session) : transmet code et
 * état (ou l'erreur d'Entra) à la page AGORA qui attend, puis ferme la fenêtre ; sinon invite à la fermer.
 */
export function RetourVerification() {
  const { t } = useT();
  const [erreur] = React.useState(() => new URLSearchParams(window.location.search).get('error'));
  React.useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const message: MessageVerification = {
      type: 'retour',
      code: q.get('code') ?? undefined,
      etat: q.get('state') ?? undefined,
      erreur: q.get('error') ?? undefined,
    };
    // Repli dans l'onglet : le résultat attend la page de départ (sessionStorage), où FinVerification le termine.
    const repli = lireSession<{ retour: string }>(CLE_REPLI);
    if (repli) {
      sessionStorage.removeItem(CLE_REPLI);
      sessionStorage.setItem(CLE_RESULTAT, JSON.stringify(message));
      window.location.replace(repli.retour.startsWith('/') ? repli.retour : '/');
      return;
    }
    const canal = new BroadcastChannel(CANAL_VERIFICATION);
    canal.postMessage(message);
    canal.close();
    // Le code ne reste pas dans l'adresse de la fenêtre (historique).
    window.history.replaceState(null, '', ROUTE_VERIFICATION_RETOUR);
    if (!message.erreur) window.setTimeout(() => window.close(), 300);
  }, []);
  return (
    <div className="ag-screen ag-canvas agx-plein agx-centre">
      <section className="ag-glass-card agx-carte agx-carte-etroite" role={erreur ? 'alert' : 'status'}>
        <h1 className="agx-titre-carte">{erreur ? t('verification.titre') : t('verification.retourTitre')}</h1>
        <p>{erreur ? t('verification.retourErreur') : t('verification.retourTexte')}</p>
        <div className="agx-actions">
          <Button variant="primary" onClick={() => window.close()}>{t('commun.fermer')}</Button>
        </div>
      </section>
    </div>
  );
}

/**
 * Fin d'une vérification faite dans l'onglet (repli) : au retour sur la page de départ, termine la vérification auprès
 * de l'API et invite l'utilisateur à refaire son action. Monté une fois par la coquille de l'application connectée.
 */
export function FinVerification() {
  const { api } = useSocle();
  const { t } = useT();
  const afficher = useMessageEphemere();
  const fait = React.useRef(false);
  React.useEffect(() => {
    if (fait.current) return;
    fait.current = true;
    const m = lireSession<MessageVerification>(CLE_RESULTAT);
    if (!m) return;
    sessionStorage.removeItem(CLE_RESULTAT);
    if (m.erreur || !m.code || !m.etat) {
      afficher({ statut: 'danger', titre: t('verification.retourErreur') });
      return;
    }
    api.post<EtatVerification>('/v1/verification/retour', { code: m.code, etat: m.etat })
      .then((r) => afficher({ titre: t('verification.reessayer'), texte: r.valideJusqua ? t('verification.valide', { heure: new Date(r.valideJusqua).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }) : undefined }))
      .catch((e: unknown) => afficher({ statut: 'danger', titre: t(`erreurs.${e instanceof ErreurApi ? e.code : 'interne'}`) }));
  }, [afficher, api, t]);
  return null;
}
